// Pure formatting helpers for the owner-facing analytics panel (sharing v2).
//
// The deck-card analytics panel renders raw DeckAnalytics into an Overview, a
// slide-by-slide bar list, and a Recent viewers list. The pure mapping from the
// data to display-ready rows lives here so it unit-tests without a DOM.
//
// British spelling throughout. No emojis.

import type { DeckAnalytics, ViewerSummary, PerSlideViews } from '../api/types';
import { displayName } from './share-viewer';

/** One slide's row in the slide-by-slide list, ready to render. */
export interface SlideRow {
  /** Zero-based slide index, as stored. */
  slideIndex: number;
  /** One-based label for display ("Slide 1"). */
  label: number;
  views: number;
  /** Bar width as a 0..100 percentage of the busiest slide. */
  barPct: number;
}

/** One viewer's row in the Recent viewers list, ready to render. */
export interface ViewerRow {
  viewerId: string;
  /** Display name — given name, or 'Anonymous'. */
  name: string;
  /** Short id suffix used to disambiguate anonymous viewers ("Anonymous · a1b2"). */
  shortId: string;
  /** Composed primary label, e.g. "Ada" or "Anonymous · a1b2". */
  primary: string;
  opens: number;
  /** Furthest slide reached, one-based ("3 / 8"). Empty string when unknown. */
  progress: string;
}

/** Clamp + round a percentage into 0..100. */
function pct(numerator: number, denominator: number): number {
  if (!denominator || denominator <= 0) return 0;
  const v = (numerator / denominator) * 100;
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

/**
 * Map the per-slide view counts to display rows with bar percentages relative to
 * the busiest slide. Preserves the input order. An empty input yields [].
 */
export function toSlideRows(perSlide: ReadonlyArray<PerSlideViews> | undefined): SlideRow[] {
  const rows = perSlide ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.views ?? 0), 0);
  return rows.map((r) => ({
    slideIndex: r.slideIndex,
    label: r.slideIndex + 1,
    views: r.views ?? 0,
    barPct: pct(r.views ?? 0, max),
  }));
}

/**
 * Map a recent viewer to a display row.
 *
 * `total` is the deck's slide count (used for the "N / total" progress label).
 * When total is unknown / non-positive, progress is just "maxSlide+1". When the
 * viewer hasn't reached any slide (maxSlide < 0), progress is empty.
 */
export function toViewerRow(v: ViewerSummary, total?: number): ViewerRow {
  const name = displayName(v.name);
  const shortId = (v.viewerId ?? '').slice(0, 4);
  const isAnon = name === 'Anonymous';
  const primary = isAnon && shortId ? `Anonymous · ${shortId}` : name;
  const maxSlide = typeof v.maxSlide === 'number' ? v.maxSlide : -1;
  let progress = '';
  if (maxSlide >= 0) {
    const reached = maxSlide + 1;
    progress = total && total > 0 ? `${reached} / ${total}` : `${reached}`;
  }
  return {
    viewerId: v.viewerId,
    name,
    shortId,
    primary,
    opens: v.opens ?? 0,
    progress,
  };
}

/** Map all recent viewers to rows. Preserves input order. */
export function toViewerRows(
  viewers: ReadonlyArray<ViewerSummary> | undefined,
  total?: number,
): ViewerRow[] {
  return (viewers ?? []).map((v) => toViewerRow(v, total));
}

/** The headline overview numbers, with safe fallbacks for missing fields. */
export interface AnalyticsOverview {
  totalOpens: number;
  uniqueViewers: number;
}

export function toOverview(a: DeckAnalytics | null | undefined): AnalyticsOverview {
  return {
    totalOpens: a?.totalOpens ?? 0,
    uniqueViewers: a?.uniqueViewers ?? 0,
  };
}

/** True when there is no recorded activity at all (drives the empty state). */
export function isAnalyticsEmpty(a: DeckAnalytics | null | undefined): boolean {
  if (!a) return true;
  const opens = a.totalOpens ?? 0;
  const viewers = a.uniqueViewers ?? 0;
  const slideViews = (a.perSlide ?? []).reduce((s, r) => s + (r.views ?? 0), 0);
  return opens === 0 && viewers === 0 && slideViews === 0;
}
